import React, { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import OrganizationService from '../../services/organizationService';
import UserService from '../../services/userService';
import ToastContainer from '../common/ToastContainer';
import { useToast } from '../../hooks/useToast';
import './Clinic.css';


const Clinic = () => {
  const auth = useAuth();
  
  // Debug logging for render cycles
  const renderCount = React.useRef(0);
  renderCount.current += 1;
  const renderLogId = `Render-${renderCount.current}`;
  
  console.log(`[Clinic] ${renderLogId}: Rendering`, { 
    isAuthenticated: auth.isAuthenticated, 
    hasUser: !!auth.user,
    userId: auth.user?.profile?.sub,
    token: auth.user?.access_token ? 'Token Present' : 'No Token'
  });

  // We use this ref for event handlers (submit/cancel)
  const isMountedRef = React.useRef(true);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    fiscalId: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    timezone: 'America/Mexico_City',
    whatsappDefaultMessage: 'Hola {nombre}, te contacto desde la clínica dental.',
    isPubliclyVisible: false,
    publicServices: [],
    socialMediaLinks: []
  });
  const [organizationId, setOrganizationId] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isCreating, setIsCreating] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [originalFormData, setOriginalFormData] = useState(null); // Backup for cancel
  const [originalLogoPreview, setOriginalLogoPreview] = useState(null); // Backup for cancel
  const { toasts, removeToast, error: showError } = useToast();

  // Load user's organization on component mount
  useEffect(() => {
    let isSubscribed = true;
    const executionId = `Load-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    console.log(`[Clinic] ${executionId}: useEffect triggered.`, {
        isAuthenticated: auth.isAuthenticated,
        hasUser: !!auth.user
    });

    const loadData = async () => {
        const token = auth.user?.access_token;
        const userId = auth.user?.profile?.sub;

        console.log(`[Clinic] ${executionId}: Auth Check`, { hasToken: !!token, userId });

        if (!token || !userId) {
            console.log(`[Clinic] ${executionId}: Missing credentials, aborting`);
            if (isSubscribed) setInitialLoading(false);
            return;
        }

        try {
            console.log(`[Clinic] ${executionId}: Calling OrganizationService.getCurrentUserOrganization...`);
            const userOrg = await OrganizationService.getCurrentUserOrganization(userId, token);
            
            console.log(`[Clinic] ${executionId}: OrganizationService returned`, { 
                found: !!userOrg, 
                orgId: userOrg?.id,
                isSubscribed 
            });

            // Fetch user profile to get appRole
            console.log(`[Clinic] ${executionId}: Calling UserService.getUserById...`);
            const profile = await UserService.getUserById(userId, token);
            
            if (!isSubscribed) {
                console.log(`[Clinic] ${executionId}: Effect cleaned up (unmounted/changed), skipping update`);
                return;
            }

            const userRole = profile?.appRole || 'user';
            
            setCanEdit(userRole === 'owner' || userRole === 'admin');

            if (userOrg) {
                setOrganizationId(userOrg.id);
                setFormData({
                name: userOrg.name || '',
                email: userOrg.email || '',
                phoneNumber: userOrg.phoneNumber || '',
                fiscalId: userOrg.fiscalId || '',
                address: {
                    street: userOrg.address?.street || '',
                    city: userOrg.address?.city || '',
                    state: userOrg.address?.state || '',
                    zipCode: userOrg.address?.zipCode || '',
                    country: userOrg.address?.country || ''
                },
                timezone: userOrg.timezone || 'America/Mexico_City',
                whatsappDefaultMessage: userOrg.whatsappDefaultMessage || 'Hola {nombre}, te contacto desde la clínica dental.',
                isPubliclyVisible: userOrg.isPubliclyVisible || false,
                publicServices: userOrg.publicServices || [],
                socialMediaLinks: userOrg.socialMediaLinks || [],
                logoUrl: userOrg.logoUrl || ''
                });

                // Set logo preview if available
                if (userOrg.logoViewUrl) {
                    setLogoPreview(userOrg.logoViewUrl);
                }
            } else {
                // No organization found, user can choose to create one
                setIsCreating(false);
            }
        } catch (error) {
            console.error(`[Clinic] ${executionId}: Error`, error);
            if (isSubscribed) {
                setMessage({
                    type: 'error',
                    text: 'Error cargando información de la clínica'
                });
            }
        } finally {
            if (isSubscribed) {
                setInitialLoading(false);
            }
        }
    };

    if (auth.isAuthenticated && auth.user) {
        loadData();
    } else {
        if (isSubscribed) setInitialLoading(false);
    }

    return () => {
        console.log(`[Clinic] ${executionId}: CLEANUP initiated`);
        isSubscribed = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.user?.access_token, auth.user?.profile?.sub]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        showError('El archivo es demasiado grande. El tamaño máximo es 2MB.');
        return;
      }

      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        showError('Formato no válido. Solo se permiten archivos JPG y PNG.');
        return;
      }

      setLogoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[Clinic] handleSubmit: Starting form submission');
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      console.log('[Clinic] handleSubmit: Token available:', !!token);
      console.log('[Clinic] handleSubmit: Organization ID:', organizationId);
      console.log('[Clinic] handleSubmit: Is Creating:', isCreating);

      // Clean up formData before submission
      let cleanedFormData = {
        ...formData,
        socialMediaLinks: formData.socialMediaLinks.map(link => {
          const cleanedLink = {
            platform: link.platform,
            url: link.url
          };

          // Only include displayText if it has a value
          if (link.displayText && link.displayText.trim()) {
            cleanedLink.displayText = link.displayText.trim();
          }

          return cleanedLink;
        })
      };

      // Handle logo upload if a new file is selected
      if (logoFile) {
        if (!token) throw new Error('No authentication token');

        const orgId = isCreating ? 'temp' : organizationId;
        const { uploadUrl, key } = await OrganizationService.getLogoUploadUrl(
          orgId,
          token,
          logoFile.type,
          logoFile.name
        );

        await OrganizationService.uploadLogo(uploadUrl, logoFile);
        cleanedFormData.logoUrl = key;
      }

      console.log('[Clinic] handleSubmit: Cleaned form data:', cleanedFormData);

      if (isCreating) {
        // Create new organization
        console.log('[Clinic] handleSubmit: Calling createOrganization');
        const newOrg = await OrganizationService.createOrganization(cleanedFormData, token);
        console.log('[Clinic] handleSubmit: Organization created:', newOrg);
        
        setOrganizationId(newOrg.id);
        setIsCreating(false);
        setMessage({
          type: 'success',
          text: 'Clínica creada exitosamente'
        });
      } else {
        // Update existing organization
        console.log('[Clinic] handleSubmit: Calling updateOrganization');
        const updatedOrg = await OrganizationService.updateOrganization(organizationId, cleanedFormData, token);
        console.log('[Clinic] handleSubmit: Organization updated:', updatedOrg);

        // Update form data with the response to ensure consistency
        if (updatedOrg) {
          setFormData({
            name: updatedOrg.name || '',
            email: updatedOrg.email || '',
            phoneNumber: updatedOrg.phoneNumber || '',
            fiscalId: updatedOrg.fiscalId || '',
            address: {
              street: updatedOrg.address?.street || '',
              city: updatedOrg.address?.city || '',
              state: updatedOrg.address?.state || '',
              zipCode: updatedOrg.address?.zipCode || '',
              country: updatedOrg.address?.country || ''
            },
            timezone: updatedOrg.timezone || 'America/Mexico_City',
            whatsappDefaultMessage: updatedOrg.whatsappDefaultMessage || 'Hola {nombre}, te contacto desde la clínica dental.',
            isPubliclyVisible: updatedOrg.isPubliclyVisible || false,
            publicServices: updatedOrg.publicServices || [],
            socialMediaLinks: updatedOrg.socialMediaLinks || [],
            logoUrl: updatedOrg.logoUrl || ''
          });

          // Update logo preview if available
          if (updatedOrg.logoViewUrl) {
            setLogoPreview(updatedOrg.logoViewUrl);
          }
        }
        
        setMessage({
          type: 'success',
          text: 'Información de clínica actualizada exitosamente'
        });
      }

      console.log('[Clinic] handleSubmit: Success, setting isEditing to false');
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('[Clinic] handleSubmit: Error occurred:', error);
      setMessage({
        type: 'error',
        text: `Error al guardar la información: ${error.message || 'Inténtalo de nuevo.'}`
      });
    } finally {
      console.log('[Clinic] handleSubmit: Finally block, setting isLoading to false');
      setIsLoading(false);
      setIsEditing(false);
    }
  };

  const handleEdit = () => {
    // Save current data as backup before editing
    setOriginalFormData({ ...formData });
    setOriginalLogoPreview(logoPreview);
    setIsEditing(true);
  };

  const handleCancel = () => {
    // Restore original data immediately
    if (originalFormData) {
      setFormData(originalFormData);
      setLogoPreview(originalLogoPreview);
      setLogoFile(null);
    }

    // Reset UI states
    setIsEditing(false);
    setIsCreating(false);
    setIsLoading(false);
    setMessage({ type: '', text: '' });

    // Clear backups
    setOriginalFormData(null);
    setOriginalLogoPreview(null);
  };

  return (
    <div className="clinic-container">
      <div className="clinic-header">
        <h1>Información de la Clínica</h1>
        <p>Gestiona la información de tu clínica dental</p>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {initialLoading ? (
        <div className="clinic-loading">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="clinic-content">
          {!organizationId && !isCreating ? (
            <div className="clinic-card">
              <div className="clinic-empty-state">
                <div className="empty-state-icon">
                  <i className="fas fa-clinic-medical"></i>
                </div>
                <h2>No estás asignado a una clínica</h2>
                <p>Contacta a soporte para solucionar este problema. soporte@digitaport.com</p>
              </div>
            </div>
          ) : (
            <div className="clinic-card">
              <div className="clinic-avatar-section">
                <div className="clinic-avatar">
                  <i className="fas fa-clinic-medical"></i>
                </div>
                <div className="clinic-info">
                  <h2>{formData.name || 'Nueva Clínica'}</h2>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="clinic-form">
                <div className="form-section">
                  <h3>Información General</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Nombre de la Clínica *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={!isEditing && !isCreating}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Correo Electrónico</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={!isEditing && !isCreating}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phoneNumber">Teléfono</label>
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        disabled={!isEditing && !isCreating}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="fiscalId">RFC / Identificación Fiscal</label>
                      <input
                        type="text"
                        id="fiscalId"
                        name="fiscalId"
                        value={formData.fiscalId}
                        onChange={handleInputChange}
                        disabled={!isEditing && !isCreating}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Dirección</h3>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label htmlFor="address.street">Calle y Número</label>
                      <input
                        type="text"
                        id="address.street"
                        name="address.street"
                        value={formData.address.street}
                        onChange={handleInputChange}
                        disabled={!isEditing && !isCreating}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="address.city">Ciudad</label>
                      <input
                        type="text"
                        id="address.city"
                        name="address.city"
                        value={formData.address.city}
                        onChange={handleInputChange}
                        disabled={!isEditing && !isCreating}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="address.state">Estado/Provincia</label>
                      <input
                        type="text"
                        id="address.state"
                        name="address.state"
                        value={formData.address.state}
                        onChange={handleInputChange}
                        disabled={!isEditing && !isCreating}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="address.zipCode">Código Postal</label>
                      <input
                        type="text"
                        id="address.zipCode"
                        name="address.zipCode"
                        value={formData.address.zipCode}
                        onChange={handleInputChange}
                        disabled={!isEditing && !isCreating}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="address.country">País</label>
                      <input
                        type="text"
                        id="address.country"
                        name="address.country"
                        value={formData.address.country}
                        onChange={handleInputChange}
                        disabled={!isEditing && !isCreating}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Configuración Regional</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="timezone">
                        <i className="fas fa-globe"></i>
                        Zona Horaria
                      </label>
                      <select
                        id="timezone"
                        name="timezone"
                        value={formData.timezone}
                        onChange={handleInputChange}
                        disabled={!isEditing && !isCreating}
                        required
                      >
                        <option value="America/New_York">Este (UTC-5/UTC-4)</option>
                        <option value="America/Chicago">Central (UTC-6/UTC-5)</option>
                        <option value="America/Denver">Montaña (UTC-7/UTC-6)</option>
                        <option value="America/Los_Angeles">Pacífico (UTC-8/UTC-7)</option>
                        <option value="America/Mexico_City">México Central (UTC-6/UTC-5)</option>
                        <option value="America/Tijuana">México Pacífico (UTC-8/UTC-7)</option>
                        <option value="America/Cancun">México Este (UTC-5)</option>
                        <option value="America/Bogota">Colombia (UTC-5)</option>
                        <option value="America/Lima">Perú (UTC-5)</option>
                        <option value="America/Argentina/Buenos_Aires">Argentina (UTC-3)</option>
                        <option value="America/Sao_Paulo">Brasil (UTC-3)</option>
                        <option value="Europe/Madrid">España (UTC+1/UTC+2)</option>
                        <option value="UTC">UTC (Tiempo Universal)</option>
                      </select>
                      <small className="form-help">
                        La zona horaria se usa para mostrar las fechas y horas en todo el sistema
                      </small>
                    </div>

                    <div className="form-group">
                      <div className="timezone-info" style={{ 
                        padding: '1rem', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '4px',
                        marginTop: '1.7rem'
                      }}>
                        <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Hora actual:</strong>
                        <span>{new Date().toLocaleString('es-ES', {
                          timeZone: formData.timezone,
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          timeZoneName: 'short'
                        })}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Configuración de WhatsApp</h3>
                  <p className="section-description">
                    Personaliza el mensaje por defecto que se envía al contactar pacientes por WhatsApp
                  </p>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label htmlFor="whatsappDefaultMessage">
                        <i className="fab fa-whatsapp"></i>
                        Mensaje por Defecto
                      </label>
                      <textarea
                        id="whatsappDefaultMessage"
                        name="whatsappDefaultMessage"
                        value={formData.whatsappDefaultMessage}
                        onChange={handleInputChange}
                        disabled={!isEditing && !isCreating}
                        rows="3"
                        maxLength="500"
                        placeholder="Hola {nombre}, te contacto desde la clínica dental."
                      />
                      <small className="form-help">
                        Usa {'{nombre}'} para insertar automáticamente el nombre del paciente. Máximo 500 caracteres.
                      </small>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Logotipo de la Clínica</h3>
                  <p className="section-description">
                    Sube el logotipo de tu clínica para que aparezca en todos los documentos generados
                  </p>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <div className="logo-upload-container">
                        {logoPreview && (
                          <div className="logo-preview">
                            <img src={logoPreview} alt="Logo preview" />
                            <button
                              type="button"
                              className="remove-logo-btn"
                              onClick={() => {
                                setLogoFile(null);
                                setLogoPreview(null);
                                setFormData(prev => ({ ...prev, logoUrl: '' }));
                              }}
                              disabled={!isEditing && !isCreating}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        )}
                        <input
                          type="file"
                          id="logo"
                          accept="image/jpeg, image/png"
                          onChange={handleLogoChange}
                          className="file-input"
                          disabled={!isEditing && !isCreating}
                        />
                        <small className="form-help">
                          Sube tu logotipo en formato JPG o PNG (máx. 2MB)
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <ToastContainer toasts={toasts} removeToast={removeToast} />

                <div className="form-actions">
                  {!isEditing && !isCreating ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleEdit}
                      disabled={!canEdit}
                      title={!canEdit ? "No tienes permisos para editar la información de la clínica" : ""}
                    >
                      <i className="fas fa-edit"></i>
                      Editar Información
                    </button>
                  ) : (
                    <div className="edit-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCancel}
                        disabled={isLoading}
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <div className="loading-spinner"></div>
                            {isCreating ? 'Creando...' : 'Guardando...'}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save"></i>
                            {isCreating ? 'Crear Clínica' : 'Guardar Cambios'}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </form >
            </div >
          )
          }
        </div >
      )}
    </div >
  );
};

export default Clinic;
